import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Chip, IconButton, Collapse, Skeleton,
  TextField, InputAdornment, Paper, Button, MenuItem, Card, Tooltip
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import InboxIcon from '@mui/icons-material/Inbox';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import { tableLabel } from '../config/tableLabels';
import { fetchRecords } from '../services/api';

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB'); // DD/MM/YYYY
};

const fmtINR = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

// Sort function for MonthKeys (latest first)
const sortMonthKeys = (a, b) => {
  if (a === 'Unknown') return 1;
  if (b === 'Unknown') return -1;
  return new Date(b).getTime() - new Date(a).getTime();
};

export default function PayrollArtifactsGrouped({ records, tableName, schema, onEdit, onDelete, onAdd, onView, onDownloadPDF }) {
  const [expandedPeriod, setExpandedPeriod] = useState(null);
  const [search, setSearch] = useState('');
  
  // Filters
  const [filterPeriod, setFilterPeriod] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  
  // Reference Maps
  const [assignments, setAssignments] = useState([]);
  const [persons, setPersons] = useState([]);
  const [runs, setRuns] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [allPayments, setAllPayments] = useState([]); // Needed for BANK_PAYMENT_FILE
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetchRecords('ASSIGNMENT').catch(() => ({ data: { data: [] } })),
      fetchRecords('PERSON').catch(() => ({ data: { data: [] } })),
      fetchRecords('PAYROLL_RUN').catch(() => ({ data: { data: [] } })),
      fetchRecords('PAY_PERIOD').catch(() => ({ data: { data: [] } })),
      fetchRecords('COMPANY').catch(() => ({ data: { data: [] } })),
      fetchRecords('PERSONAL_PAYMENT_METHOD').catch(() => ({ data: { data: [] } })),
      tableName === 'BANK_PAYMENT_FILE' 
        ? fetchRecords('PAYMENT').catch(() => ({ data: { data: [] } }))
        : Promise.resolve({ data: { data: [] } })
    ]).then(([a, p, r, pe, c, ppm, payReq]) => {
      setAssignments(a.data.data || []);
      setPersons(p.data.data || []);
      setRuns(r.data.data || []);
      setPeriods(pe.data.data || []);
      setCompanies(c.data.data || []);
      setPaymentMethods(ppm.data.data || []);
      setAllPayments(payReq.data.data || []);
      setLoading(false);
    });
  }, [tableName]);

  const assignmentMap = useMemo(() => Object.fromEntries(assignments.map(a => [a.assignment_id, a])), [assignments]);
  const personMap = useMemo(() => Object.fromEntries(persons.map(p => [p.person_id, p])), [persons]);
  const runMap = useMemo(() => Object.fromEntries(runs.map(r => [r.run_id, r])), [runs]);
  const periodMap = useMemo(() => Object.fromEntries(periods.map(p => [p.period_id, p])), [periods]);
  const ppmMap = useMemo(() => {
    const map = {};
    paymentMethods.forEach(ppm => {
      // Prioritize primary methods or just take the first one
      if (!map[ppm.assignment_id]) map[ppm.assignment_id] = ppm;
    });
    return map;
  }, [paymentMethods]);

  // Derive Period Name Helper
  const getPeriodForRun = (runId) => {
    const run = runMap[runId];
    if (!run) return 'Unknown Period';
    const period = periodMap[run.period_id];
    if (!period) return 'Unknown Period';
    // Format to "May 2026"
    return new Date(period.start_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  // Enhance Records with Assignment Name and Bank Info
  const enrichedRecords = useMemo(() => {
    if (tableName === 'BANK_PAYMENT_FILE') {
       // Bank Payment Files don't have assignment_id. We just enrich with Period
       return records.map(r => {
         const pName = getPeriodForRun(r.run_id);
         const paymentsInRun = allPayments.filter(p => p.run_id === r.run_id).map(p => {
            const asn = assignmentMap[p.assignment_id];
            const per = asn ? personMap[asn.person_id] : null;
            const ppm = ppmMap[p.assignment_id] || {};
            const fullName = per ? `${per.first_name || ''} ${per.last_name || ''}`.trim() : (asn?.employee_code || 'Unknown');
            return {
              ...p,
              assignmentName: `${p.assignment_id} — ${fullName}`,
              person_id: per?.person_id || '—',
              bank_account_id: ppm.bank_id || '—', // using bank_id for display if account not explicitly present
              org_payment_method: ppm.payment_type || p.payment_method || '—',
            };
         });
         return { ...r, periodName: pName, linkedPayments: paymentsInRun };
       });
    }

    // For PAYMENT and PAYSLIP
    return records.map(r => {
      const pName = r.period_name || getPeriodForRun(r.run_id);
      const asn = assignmentMap[r.assignment_id];
      const per = asn ? personMap[asn.person_id] : null;
      const ppm = ppmMap[r.assignment_id] || {};
      const fullName = per ? `${per.first_name || ''} ${per.last_name || ''}`.trim() : (asn?.employee_code || 'Unknown');
      
      return {
        ...r,
        periodName: pName,
        assignmentName: `${r.assignment_id} — ${fullName}`,
        person_id: per?.person_id || '—',
        fullName: fullName,
        bank_account_id: ppm.bank_id || '—',
        org_payment_method: ppm.payment_type || r.payment_method || '—',
      };
    });
  }, [records, tableName, assignmentMap, personMap, runMap, periodMap, ppmMap, allPayments]);

  // Group by Period
  const periodGroups = useMemo(() => {
    const groups = {};
    for (const r of enrichedRecords) {
      if (!groups[r.periodName]) {
        groups[r.periodName] = {
          periodName: r.periodName,
          records: [],
          stats: { count: 0, gross: 0, net: 0, deductions: 0, amount: 0 }
        };
      }
      groups[r.periodName].records.push(r);
      groups[r.periodName].stats.count++;
      
      if (tableName === 'PAYMENT') {
        groups[r.periodName].stats.gross += Number(r.gross_amount || 0);
        groups[r.periodName].stats.deductions += Number(r.total_deductions || 0);
        groups[r.periodName].stats.net += Number(r.net_amount || 0);
      } else if (tableName === 'PAYSLIP') {
        // Payslip might have net_payable
      } else if (tableName === 'BANK_PAYMENT_FILE') {
        groups[r.periodName].stats.amount += Number(r.total_amount || 0);
      }
    }
    
    return Object.values(groups).sort((a, b) => sortMonthKeys(a.periodName, b.periodName));
  }, [enrichedRecords, tableName]);

  const periodOptions = useMemo(() => periodGroups.map(g => g.periodName), [periodGroups]);

  // Expand the latest period by default
  useEffect(() => {
    if (!loading && periodGroups.length > 0 && !expandedPeriod && !filterPeriod) {
      setExpandedPeriod(periodGroups[0].periodName);
    }
  }, [loading, periodGroups, expandedPeriod, filterPeriod]);

  // Search & Filter
  const filteredGroups = useMemo(() => {
    let result = periodGroups;
    if (filterPeriod) result = result.filter(g => g.periodName === filterPeriod);
    
    result = result.map(g => {
      let filteredRecs = g.records;
      
      if (filterCompany) filteredRecs = filteredRecs.filter(r => r.COMPANY_id === filterCompany);
      
      if (filterStatus) {
        if (tableName === 'PAYMENT') filteredRecs = filteredRecs.filter(r => r.payment_status === filterStatus);
        else if (tableName === 'PAYSLIP') filteredRecs = filteredRecs.filter(r => r.status === filterStatus);
        else if (tableName === 'BANK_PAYMENT_FILE') filteredRecs = filteredRecs.filter(r => r.file_status === filterStatus);
      }
      
      const q = search.trim().toLowerCase();
      if (q) {
        filteredRecs = filteredRecs.filter(r => {
          if (tableName === 'BANK_PAYMENT_FILE') {
             return r.file_name?.toLowerCase().includes(q) || r.file_id?.toLowerCase().includes(q) ||
                    r.linkedPayments?.some(lp => lp.assignmentName.toLowerCase().includes(q) || lp.person_id.toLowerCase().includes(q));
          }
          return r.assignmentName?.toLowerCase().includes(q) ||
                 r.person_id?.toLowerCase().includes(q) ||
                 r.payment_id?.toLowerCase().includes(q) ||
                 r.payslip_id?.toLowerCase().includes(q) ||
                 r.bank_account_id?.toLowerCase().includes(q);
        });
      }
      
      return { ...g, records: filteredRecs };
    }).filter(g => g.records.length > 0);

    return result;
  }, [periodGroups, filterPeriod, filterCompany, filterStatus, search, tableName]);

  const totalRecords = filteredGroups.reduce((sum, g) => sum + g.records.length, 0);

  if (loading) {
    return (
      <Box><Skeleton height={80} sx={{ mb: 2, borderRadius: 2 }} /><Skeleton height={80} sx={{ mb: 2, borderRadius: 2 }} /></Box>
    );
  }

  // --- Render Functions ---

  const renderPaymentRow = (r, idx, isLast) => (
    <Box key={r.payment_id || idx} sx={{ 
      display: 'flex', alignItems: 'center', px: 3, py: 1.5, 
      borderBottom: isLast ? 'none' : '1px solid #F1F5F9',
      '&:hover': { bgcolor: '#F8FAFC' }
    }}>
      <Box sx={{ width: 160 }}>
        <Typography fontSize={13} fontWeight={600} color="#0F172A" noWrap>{r.fullName}</Typography>
        <Typography fontSize={11} color="#64748B" sx={{ fontFamily: 'monospace' }}>{r.assignment_id} · {r.person_id}</Typography>
      </Box>
      <Box sx={{ width: 130 }}>
        <Typography fontSize={12} color="#475569">{r.bank_account_id}</Typography>
        <Typography fontSize={11} color="#64748B">{r.org_payment_method}</Typography>
      </Box>
      <Typography fontSize={13} fontWeight={600} color="#111827" sx={{ width: 100, textAlign: 'right' }}>₹{fmtINR(r.gross_amount)}</Typography>
      <Typography fontSize={13} fontWeight={600} color="#DC2626" sx={{ width: 100, textAlign: 'right' }}>−₹{fmtINR(r.total_deductions)}</Typography>
      <Typography fontSize={13} fontWeight={800} color="#16A34A" sx={{ width: 110, textAlign: 'right' }}>₹{fmtINR(r.net_amount)}</Typography>
      <Typography fontSize={12} color="#64748B" sx={{ width: 100, textAlign: 'center' }}>{fmtDate(r.payment_date)}</Typography>
      
      <Box sx={{ width: 100, textAlign: 'center' }}>
        <Chip label={r.payment_status || 'PENDING'} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, 
          bgcolor: r.payment_status === 'PAID' ? '#DCFCE7' : '#FEF3C7',
          color: r.payment_status === 'PAID' ? '#15803D' : '#92400E' }} />
      </Box>

      <Stack direction="row" spacing={0.5} sx={{ flex: 1, justifyContent: 'flex-end' }}>
        {onView && <IconButton size="small" onClick={(e) => { e.stopPropagation(); onView(r); }} sx={{ color: '#64748B', bgcolor: '#F1F5F9' }}><VisibilityIcon sx={{ fontSize: 14 }} /></IconButton>}
        {onEdit && <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(r); }} sx={{ color: '#8B5CF6', bgcolor: '#F5F3FF' }}><EditIcon sx={{ fontSize: 14 }} /></IconButton>}
        {onDelete && <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(r); }} sx={{ color: '#EF4444', bgcolor: '#FEF2F2' }}><DeleteIcon sx={{ fontSize: 14 }} /></IconButton>}
      </Stack>
    </Box>
  );

  const renderPayslipRow = (r, idx, isLast) => (
    <Box key={r.payslip_id || idx} sx={{ 
      display: 'flex', alignItems: 'center', px: 3, py: 1.5, 
      borderBottom: isLast ? 'none' : '1px solid #F1F5F9',
      '&:hover': { bgcolor: '#F8FAFC' }
    }}>
      <Box sx={{ width: 180 }}>
        <Typography fontSize={13} fontWeight={600} color="#0F172A" noWrap>{r.fullName}</Typography>
        <Typography fontSize={11} color="#64748B" sx={{ fontFamily: 'monospace' }}>{r.assignment_id} · {r.person_id}</Typography>
      </Box>
      <Typography fontSize={13} color="#475569" sx={{ width: 120, fontFamily: 'monospace' }}>{r.payslip_id}</Typography>
      <Typography fontSize={13} fontWeight={600} color="#111827" sx={{ width: 100, textAlign: 'right' }}>₹{fmtINR(r.gross_earnings)}</Typography>
      <Typography fontSize={13} fontWeight={600} color="#DC2626" sx={{ width: 100, textAlign: 'right' }}>−₹{fmtINR(r.total_deductions)}</Typography>
      <Typography fontSize={13} fontWeight={800} color="#16A34A" sx={{ width: 110, textAlign: 'right' }}>₹{fmtINR(r.net_payable)}</Typography>
      <Box sx={{ width: 100, textAlign: 'center' }}>
        <Chip label={r.status || 'DRAFT'} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, 
          bgcolor: r.status === 'PUBLISHED' ? '#DCFCE7' : r.status === 'GENERATED' ? '#DBEAFE' : '#F1F5F9',
          color: r.status === 'PUBLISHED' ? '#15803D' : r.status === 'GENERATED' ? '#1D4ED8' : '#64748B' }} />
      </Box>

      <Stack direction="row" spacing={1} sx={{ flex: 1, justifyContent: 'flex-end' }}>
        {onView && <Button size="small" variant="outlined" startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />} onClick={(e) => { e.stopPropagation(); onView(r); }} sx={{ fontSize: 11, py: 0.2, mr: 1, color: '#64748B', borderColor: '#CBD5E1' }}>View EYE</Button>}
        {onDownloadPDF && <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); onDownloadPDF(r); }} sx={{ fontSize: 11, py: 0.2 }}>View PDF</Button>}
        {onEdit && <Tooltip title="Regenerate"><IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(r); }} sx={{ color: '#3B82F6', bgcolor: '#EFF6FF' }}><RefreshIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>}
      </Stack>
    </Box>
  );

  const renderBankFileRow = (file, idx, isLast) => (
    <Box key={file.file_id || idx} sx={{ borderBottom: isLast ? 'none' : '2px solid #E2E8F0', mb: isLast ? 0 : 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 2, bgcolor: '#F8FAFC' }}>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="center" gap={1.5} mb={0.5}>
            <Typography fontSize={15} fontWeight={700} color="#0F172A">{file.file_name}</Typography>
            <Chip label={file.file_type} size="small" sx={{ height: 20, fontSize: 10, bgcolor: '#EEF2FF', color: '#4F46E5', fontWeight: 700 }} />
            <Chip label={file.file_status} size="small" sx={{ height: 20, fontSize: 10, bgcolor: file.file_status==='GENERATED'?'#DCFCE7':'#FEF3C7', color: file.file_status==='GENERATED'?'#15803D':'#92400E', fontWeight: 700 }} />
          </Stack>
          <Typography fontSize={12} color="#64748B">Sender AC: {file.sender_account} · {file.total_records} Records · ₹{fmtINR(file.total_amount)}</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {onView && <Button size="small" variant="contained" startIcon={<VisibilityIcon />} onClick={(e) => { e.stopPropagation(); onView(file); }} sx={{ fontSize: 11, bgcolor: '#8B5CF6', '&:hover': { bgcolor: '#7C3AED' } }}>View Metadata</Button>}
          <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={(e) => { e.stopPropagation(); if (onDownloadPDF) onDownloadPDF(file); }} sx={{ fontSize: 11 }}>Download File</Button>
          {onEdit && <Tooltip title="Regenerate"><IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(file); }} sx={{ color: '#3B82F6', bgcolor: '#EFF6FF' }}><RefreshIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>}
        </Stack>
      </Box>

      {/* Embedded Payments Table */}
      <Box sx={{ px: 3, py: 1 }}>
        <Typography fontSize={11} fontWeight={700} color="#94A3B8" sx={{ mb: 1, mt: 1 }}>LINKED PAYMENTS ({file.linkedPayments?.length || 0})</Typography>
        <Box sx={{ border: '1px solid #F1F5F9', borderRadius: '8px', overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', px: 2, py: 1, bgcolor: '#F8FAFC', borderBottom: '1px solid #F1F5F9' }}>
             <Typography fontSize={10.5} fontWeight={700} color="#64748B" sx={{ width: 180 }}>EMPLOYEE</Typography>
             <Typography fontSize={10.5} fontWeight={700} color="#64748B" sx={{ width: 140 }}>BANK ACC</Typography>
             <Typography fontSize={10.5} fontWeight={700} color="#64748B" sx={{ width: 120 }}>METHOD</Typography>
             <Typography fontSize={10.5} fontWeight={700} color="#64748B" sx={{ width: 100, textAlign: 'right' }}>AMOUNT</Typography>
             <Typography fontSize={10.5} fontWeight={700} color="#64748B" sx={{ flex: 1, textAlign: 'center' }}>STATUS</Typography>
          </Box>
          {file.linkedPayments?.map((lp, lidx) => (
             <Box key={lidx} sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, borderBottom: lidx === file.linkedPayments.length-1 ? 'none' : '1px solid #F1F5F9' }}>
                <Typography fontSize={12} fontWeight={600} color="#334155" sx={{ width: 180 }} noWrap>{lp.assignmentName}</Typography>
                <Typography fontSize={12} color="#475569" sx={{ width: 140 }}>{lp.bank_account_id}</Typography>
                <Typography fontSize={12} color="#475569" sx={{ width: 120 }}>{lp.org_payment_method}</Typography>
                <Typography fontSize={12} fontWeight={700} color="#16A34A" sx={{ width: 100, textAlign: 'right' }}>₹{fmtINR(lp.net_amount)}</Typography>
                <Box sx={{ flex: 1, textAlign: 'center' }}>
                   <Chip label={lp.payment_status} size="small" sx={{ height: 18, fontSize: 9, bgcolor: '#F1F5F9', color: '#475569', fontWeight: 600 }} />
                </Box>
             </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search records..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{
              width: { xs: '100%', sm: 380 },
              '& .MuiOutlinedInput-root': {
                bgcolor: 'white', fontSize: 13, borderRadius: '10px',
                '& fieldset': { borderColor: '#E8EDF5', borderWidth: '1.5px' },
              },
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94A3B8', fontSize: 17 }} /></InputAdornment>,
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')}><ClearIcon sx={{ fontSize: 15 }} /></IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <Stack direction="row" spacing={1.5}>
            <Stack direction="row" alignItems="center" gap={0.6}
              sx={{ px: 1.5, py: 0.5, borderRadius: '8px', bgcolor: 'white', border: '1.5px solid #E8EDF5' }}>
              <Typography fontSize={12} fontWeight={600} color="text.secondary">
                {totalRecords} Records
              </Typography>
            </Stack>
          </Stack>
        </Box>

        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ alignItems: 'center' }}>
          <FilterListIcon sx={{ fontSize: 20, color: '#9CA3AF' }} />
          <TextField select size="small" value={filterPeriod} onChange={e => setFilterPeriod(e.target.value)}
            sx={{ width: 160, '& .MuiOutlinedInput-root': { bgcolor: 'white', borderRadius: '8px' } }}
            InputProps={{ sx: { fontSize: 13 } }}>
            <MenuItem value="">All Pay Periods</MenuItem>
            {periodOptions.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
          </TextField>
        </Stack>
      </Box>

      {/* Accordions */}
      <Stack spacing={3}>
        {filteredGroups.length === 0 ? (
          <Paper sx={{ py: 8, textAlign: 'center', bgcolor: 'white', border: '1.5px solid #E5E7EB', borderRadius: '12px' }}>
            <Typography fontSize={14} fontWeight={600} color="#374151">No Records Found</Typography>
          </Paper>
        ) : (
          filteredGroups.map(g => {
            const isExpanded = expandedPeriod === g.periodName || filterPeriod === g.periodName;
            
            return (
              <Box key={g.periodName} sx={{ 
                bgcolor: 'white', borderRadius: '12px', border: '1.5px solid #E5E7EB',
                boxShadow: isExpanded ? '0 4px 12px rgba(0,0,0,0.03)' : 'none',
                overflow: 'hidden', transition: 'all 0.2s'
              }}>
                {/* Header */}
                <Box
                  onClick={() => setExpandedPeriod(isExpanded && !filterPeriod ? null : g.periodName)}
                  sx={{
                    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2,
                    px: 3, py: 2, cursor: filterPeriod ? 'default' : 'pointer',
                    bgcolor: isExpanded ? '#F8FAFC' : 'white',
                    borderBottom: isExpanded ? '1px solid #E5E7EB' : 'none',
                    '&:hover': { bgcolor: filterPeriod ? '#F8FAFC' : '#F1F5F9' },
                  }}
                >
                  <Box sx={{
                    width: 44, height: 44, borderRadius: '12px',
                    bgcolor: '#EFF6FF', color: '#3B82F6',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <CalendarMonthIcon sx={{ fontSize: 22 }} />
                  </Box>
                  
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography fontSize={18} fontWeight={700} color="#0F172A">{g.periodName}</Typography>
                    <Typography fontSize={12} color="#64748B" mt={0.5}>{tableLabel(tableName)} records for this period</Typography>
                  </Box>

                  {/* KPIs based on module */}
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Box sx={{ textAlign: 'center', px: 1.5, py: 0.5, bgcolor: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                      <Typography fontSize={10} color="#64748B" fontWeight={600} textTransform="uppercase">Records</Typography>
                      <Typography fontSize={13} fontWeight={700} color="#0F172A">{g.stats.count}</Typography>
                    </Box>
                    {tableName === 'PAYMENT' && (
                      <>
                        <Box sx={{ textAlign: 'center', px: 1.5, py: 0.5, bgcolor: '#F0FDF4', borderRadius: '8px', border: '1px solid #DCFCE7' }}>
                          <Typography fontSize={10} color="#16A34A" fontWeight={600} textTransform="uppercase">Gross</Typography>
                          <Typography fontSize={13} fontWeight={700} color="#15803D">₹{fmtINR(g.stats.gross)}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center', px: 1.5, py: 0.5, bgcolor: '#FEF2F2', borderRadius: '8px', border: '1px solid #FEE2E2' }}>
                          <Typography fontSize={10} color="#EF4444" fontWeight={600} textTransform="uppercase">Deductions</Typography>
                          <Typography fontSize={13} fontWeight={700} color="#B91C1C">₹{fmtINR(g.stats.deductions)}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center', px: 1.5, py: 0.5, bgcolor: '#F0FDF4', borderRadius: '8px', border: '1px solid #DCFCE7' }}>
                          <Typography fontSize={10} color="#16A34A" fontWeight={600} textTransform="uppercase">Net Pay</Typography>
                          <Typography fontSize={13} fontWeight={700} color="#15803D">₹{fmtINR(g.stats.net)}</Typography>
                        </Box>
                      </>
                    )}
                    {tableName === 'BANK_PAYMENT_FILE' && (
                      <Box sx={{ textAlign: 'center', px: 1.5, py: 0.5, bgcolor: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                         <Typography fontSize={10} color="#64748B" fontWeight={600} textTransform="uppercase">Transfer Sum</Typography>
                         <Typography fontSize={13} fontWeight={700} color="#0F172A">₹{fmtINR(g.stats.amount)}</Typography>
                      </Box>
                    )}
                  </Box>

                  {!filterPeriod && (
                    <IconButton size="small" sx={{ ml: 1, color: '#3B82F6' }}>
                      {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                  )}
                </Box>

                {/* Expanded Table */}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box sx={{ bgcolor: 'white' }}>
                    {tableName === 'PAYMENT' && (
                      <Box sx={{ display: 'flex', px: 3, py: 1.5, borderBottom: '1px solid #F1F5F9', bgcolor: '#F8FAFC' }}>
                        <Typography fontSize={11} fontWeight={700} color="#64748B" sx={{ width: 160 }}>EMPLOYEE</Typography>
                        <Typography fontSize={11} fontWeight={700} color="#64748B" sx={{ width: 130 }}>BANK & METHOD</Typography>
                        <Typography fontSize={11} fontWeight={700} color="#64748B" sx={{ width: 100, textAlign: 'right' }}>GROSS</Typography>
                        <Typography fontSize={11} fontWeight={700} color="#64748B" sx={{ width: 100, textAlign: 'right' }}>DEDUCTIONS</Typography>
                        <Typography fontSize={11} fontWeight={700} color="#64748B" sx={{ width: 110, textAlign: 'right' }}>NET PAY</Typography>
                        <Typography fontSize={11} fontWeight={700} color="#64748B" sx={{ width: 100, textAlign: 'center' }}>DATE</Typography>
                        <Typography fontSize={11} fontWeight={700} color="#64748B" sx={{ width: 100, textAlign: 'center' }}>STATUS</Typography>
                        <Typography fontSize={11} fontWeight={700} color="#64748B" sx={{ flex: 1, textAlign: 'right' }}>ACTIONS</Typography>
                      </Box>
                    )}
                    {tableName === 'PAYSLIP' && (
                      <Box sx={{ display: 'flex', px: 3, py: 1.5, borderBottom: '1px solid #F1F5F9', bgcolor: '#F8FAFC' }}>
                        <Typography fontSize={11} fontWeight={700} color="#64748B" sx={{ width: 180 }}>EMPLOYEE</Typography>
                        <Typography fontSize={11} fontWeight={700} color="#64748B" sx={{ width: 120 }}>PAYSLIP #</Typography>
                        <Typography fontSize={11} fontWeight={700} color="#64748B" sx={{ width: 100, textAlign: 'right' }}>GROSS</Typography>
                        <Typography fontSize={11} fontWeight={700} color="#64748B" sx={{ width: 100, textAlign: 'right' }}>DEDUCTIONS</Typography>
                        <Typography fontSize={11} fontWeight={700} color="#64748B" sx={{ width: 110, textAlign: 'right' }}>NET</Typography>
                        <Typography fontSize={11} fontWeight={700} color="#64748B" sx={{ width: 100, textAlign: 'center' }}>STATUS</Typography>
                        <Typography fontSize={11} fontWeight={700} color="#64748B" sx={{ flex: 1, textAlign: 'right' }}>ACTIONS</Typography>
                      </Box>
                    )}
                    
                    {g.records.map((r, idx) => {
                       if (tableName === 'PAYMENT') return renderPaymentRow(r, idx, idx === g.records.length - 1);
                       if (tableName === 'PAYSLIP') return renderPayslipRow(r, idx, idx === g.records.length - 1);
                       if (tableName === 'BANK_PAYMENT_FILE') return renderBankFileRow(r, idx, idx === g.records.length - 1);
                       return null;
                    })}
                  </Box>
                </Collapse>
              </Box>
            );
          })
        )}
      </Stack>
    </Box>
  );
}
